import { publicClient } from '@/lib/axios'
import type { CoinParams } from '@/types/api'

// Types for query parameters
interface AccountHashrateChartParams extends CoinParams {
  chart_type: 1 | 2 | 3 | 4 | 5 // 1: hourly, 2: daily, 3: hourly 7 days, 4: daily month, 5: daily year
  period?: number
}

// ===== PUBLIC API METHODS =====

/**
 * Get pool metrics (public data)
 */
export const getPoolMetrics = async (params?: { coin?: string }) => {
  return publicClient.post('/poolMetrics', params || {})
}

/**
 * Get pool hashrate chart data (public data)
 */
export const getPoolHashrateChart = async (params: {
  chart_type: 1 | 2 | 3 | 4 | 5 // 1: hourly, 2: daily, 3: hourly 7 days, 4: daily month, 5: daily year
  period?: number
  coin?: string
}) => {
  return publicClient.post('/poolHashrateChart', params)
}

/**
 * Get found blocks list (public data)
 */
export const getFoundBlocks = async (params?: { coin?: string; page?: number; limit?: number; sort_field?: string; sort_order?: string }) => {
  return publicClient.post('/foundBlocks', params || {})
}

/**
 * Get users list (contributors) - public data
 */
export const getUsers = async (params?: { page?: number; limit?: number; coin?: string; sort_field?: string; sort_order?: string }) => {
  return publicClient.post('/users', params || {})
}

/**
 * Search users by query - public data
 */
export const searchUsers = async (params: { query: string; coin?: string }) => {
  return publicClient.post('/usersSearch', params)
}

// ===== UNREG USER API METHODS =====

/**
 * Get account overview for unreg user (public data)
 */
export const getUnregUserAccountOverview = async (params: { user: string; coin?: string }) => {
  return publicClient.post('/accountOverview', params)
}

/**
 * Get account hashrate history for unreg user (public data)
 */
export const getUnregUserAccountHashrateHistory = async (params: {
  user: string
  start_date?: string
  end_date?: string
  page?: number
  limit?: number
  coin?: string
}) => {
  return publicClient.post('/accountHashrateHistory', params)
}

/**
 * Get account hashrate chart for unreg user (public data)
 */
export const getUnregUserAccountHashrateChart = async (
  params: { user: string } & AccountHashrateChartParams
) => {
  return publicClient.post('/accountHashrateChart', params)
}

/**
 * Get workers list for unreg user (public data)
 */
export const getUnregUserWorkers = async (params: { user: string; coin?: string }) => {
  return publicClient.post('/workers', params)
}

/**
 * Get worker hashrate for unreg user (public data)
 */
export const getWorkerHashrate = async (params: {
  user: string
  page?: number
  limit?: number
  coin?: string
  sort_field?: string
  sort_order?: string
}) => {
  return publicClient.post('/workerHashrate', params)
}

/**
 * Get worker overview (public data)
 */
export const getWorkerOverview = async (params: {
  user: string
  worker: string
  coin?: string
}) => {
  return publicClient.post('/workerOverview', params)
}

/**
 * Get worker hashrate history for unreg user (public data)
 */
export const getUnregWorkerHashrateHistory = async (params: {
  user: string
  worker: string
  chart_type: 1 | 2 | 3 | 4 | 5
  period?: number
  coin?: string
}) => {
  return publicClient.post('/workerHashrateHistory', params)
}

/**
 * Get payment history for unreg user (public data)
 */
export const getUnregUserPaymentHistory = async (params: {
  user: string
  start_date?: string
  end_date?: string
  range?: 'week' | 'month' | 'year'
  page?: number
  limit?: number
  coin?: string
  worker?: string
  sort_field?: string
  sort_order?: string
}) => {
  return publicClient.post('/paymentHistory', params)
}

/**
 * Get reward history for unreg user (public data)
 */
export const getUnregUserRewardHistory = async (params: {
  user: string
  start_date?: string
  end_date?: string
  range?: 'week' | 'month' | 'year'
  page?: number
  limit?: number
  coin?: string
  sort_field?: string
  sort_order?: string
  worker?: string
}) => {
  return publicClient.post('/rewardHistory', params)
}

/**
 * Get workers stats for unreg user with filters and search (public data)
 */
export const getUnregUserWorkersStats = async (params: {
  user: string
  page?: number
  limit?: number
  coin?: string
  status?: string
  search?: string
  sort_field?: string
  sort_order?: string
}) => {
  return publicClient.post('/workersStats', params)
}
